import { SelectDropdownOption } from "../components/APSelectDropDown";
import { AutoCompleteOption } from "../components/form/APAutoComplete";
import { VehicleSearchInputs } from "../dossier/vehicleSearch";
import {
  APVehicleAspectLimitMap,
  APVehicleAspectLimits,
  APVehicleAspectOptionMap,
  APVehicleAspects,
  APVehicleAspectUnitMap,
  APVehicleAspectValueLabelMap,
  APVehicleValueFormatterOptionsMap,
  APVehicleVariant,
  APVehicleVariantLabelMap,
  BodyTypeLabelMap,
  DriveDescriptionLabelMap,
  FuelTypeLabelMap,
  PowerPlantLabelMap,
  PowerPlantType,
  StandardVehicleValueFormatterOptions,
  TransmissionLabelMap,
  VehicleClassLabelMap,
  VehicleOption,
  VehicleSegment,
  VehicleSegmentLabelMap,
  VehicleVariantSearchFilters,
  VehicleVariantSearchSortOptions,
} from "../types/Vehicles";
import { buildAutoCompleteOptionsFromArray } from "./buildOptions";
import { toDollars, toFormattedNumber, toThousands } from "./currencies";
import { toYearMonthOnlyFormat, toYearOnlyFormat } from "./dates";
import { mapLabelWithFallback, merge } from "./object";
import {
  addAutoCompleteOptionToParams,
  addBetweenMultipleFilterToParams,
  addSliderRangeFilterToParams,
  getAutoCompleteOptionFromParam,
  getAutoCompleteOptionsFromParam,
  getSearchFilterFromParam,
  getSliderRangeFilterFromParam,
} from "./search";
import { ReplaceAll } from "./types";

export type VehicleVariantSearchSortOption =
  | "priceAsc"
  | "priceDesc"
  | "evRangeDesc"
  | "powerKwDesc"
  | "cargoMinDesc"
  | "makeAsc"
  | "makeDesc";

export type VehicleVariantValueFormatters = ReplaceAll<
  Pick<
    VehicleSearchInputs,
    | "priceRange"
    | "seatCapacity"
    | "powerKwRange"
    | "evRange"
    | "fuelEconomyRange"
    | "accelerationRange"
    | "warrantyYearsRange"
    | "ancapratingRange"
    | "towingUnbrakedKgRange"
    | "co2CombinedRange"
    | "powerToWeightRange"
    | "minCargoVolumeRange"
  >,
  (value: unknown) => string
>;

export type VehicleVariantFieldLabelMap = ReplaceAll<
  VehicleSearchInputs,
  string
>;

export const VehicleVariantValueFormatters: VehicleVariantValueFormatters = {
  priceRange: (value) => toThousands(value as number, true),
  seatCapacity: (value) => `${toFormattedNumber(value as number, 0)}`,
  powerKwRange: (value) =>
    `${toFormattedNumber(value as number, 0)}${APVehicleAspectUnitMap.powerKw}`,
  evRange: (value) =>
    `${toFormattedNumber(value as number, 0)}${APVehicleAspectUnitMap.range}`,
  fuelEconomyRange: (value) =>
    `${toFormattedNumber(value as number, 1)}${
      APVehicleAspectUnitMap.fuelEconomy
    }`,
  accelerationRange: (value) =>
    `${toFormattedNumber(value as number, 1)}${
      APVehicleAspectUnitMap.acceleration
    }`,
  warrantyYearsRange: (value) =>
    `${toFormattedNumber(value as number, 0)}${
      APVehicleAspectUnitMap.warrantyYears
    }`,
  ancapratingRange: (value) =>
    `${toFormattedNumber(value as number, 0)} ${
      APVehicleAspectUnitMap.ancaprating
    }`,
  towingUnbrakedKgRange: (value) =>
    `${toFormattedNumber(value as number, 0)}${
      APVehicleAspectUnitMap.towingUnbrakedKg
    }`,
  co2CombinedRange: (value) =>
    `${toFormattedNumber(value as number, 0)}${
      APVehicleAspectUnitMap.co2Combined
    }`,
  powerToWeightRange: (value) =>
    `${toFormattedNumber(value as number, 0)}${
      APVehicleAspectUnitMap.powerToWeight
    }`,
  minCargoVolumeRange: (value) =>
    `${toFormattedNumber(value as number, 0)}${
      APVehicleAspectUnitMap.minCargoVolume
    }`,
};

export const VehicleVariantFieldLabelMap: VehicleVariantFieldLabelMap = {
  year: APVehicleVariantLabelMap.year,
  segment: APVehicleVariantLabelMap.segment,
  seatCapacity: "Number of Seats",
  priceRange: APVehicleVariantLabelMap.listPriceIncGst,
  powerPlantType: APVehicleVariantLabelMap.powerPlantType,
  bodyType: APVehicleVariantLabelMap.bodyType,
  minCargoVolumeRange: "Boot Volume",
  powerKwRange: APVehicleVariantLabelMap.powerKw,
  evRange: "EV Range",
  fuelEconomyRange: "Fuel Economy (Combined)",
  make: APVehicleVariantLabelMap.make,
  accelerationRange: "0-100km sec",
  warrantyYearsRange: APVehicleVariantLabelMap.warrantyYears,
  ancapratingRange: APVehicleVariantLabelMap.ancaprating,
  towingUnbrakedKgRange: APVehicleVariantLabelMap.towingUnbrakedKg,
  co2CombinedRange: APVehicleVariantLabelMap.co2Combined,
  powerToWeightRange: APVehicleVariantLabelMap.powerToWeight,
  driveDescription: APVehicleVariantLabelMap.driveDescription,
  fuelType: APVehicleVariantLabelMap.fuelType,
  transmission: APVehicleVariantLabelMap.transmission,
};

export const VehicleVariantSearchSortOptionsList: SelectDropdownOption<VehicleVariantSearchSortOption>[] =
  [
    { id: "priceDesc", label: "Price - High to Low" },
    { id: "priceAsc", label: "Price - Low to High" },
    { id: "evRangeDesc", label: "Range" },
    { id: "powerKwDesc", label: "Power Kw" },
    { id: "cargoMinDesc", label: "Boot Volume" },
    { id: "makeAsc", label: "Make - A to Z" },
    { id: "makeDesc", label: "Make - Z to A" },
  ];

export const DEFAULT_VEHICLE_ASPECT_LIMITS: Record<
  keyof APVehicleAspectLimitMap,
  APVehicleAspectLimits
> = {
  listPriceIncGst: {
    min: 10000,
    max: 350000,
    step: 5000,
    defaultStart: 80000,
    defaultEnd: 10000,
  },
  minCargoVolume: {
    min: 100,
    max: 1500,
    step: 100,
  },
  powerKw: {
    min: 100,
    max: 300,
    step: 25,
  },
  range: {
    min: 200,
    max: 600,
    step: 50,
  },
  fuelEconomy: {
    min: 1,
    max: 15,
    step: 1,
  },
  acceleration: {
    min: 1,
    max: 15,
    step: 1,
  },
  warrantyYears: {
    min: 1,
    max: 10,
    step: 1,
  },
  ancaprating: {
    min: 1,
    max: 5,
    step: 1,
  },
  towingUnbrakedKg: {
    min: 0,
    max: 750,
    step: 50,
  },
  co2Combined: {
    min: 25,
    max: 300,
    step: 25,
  },
  powerToWeight: {
    min: 25,
    max: 250,
    step: 25,
  },
};

export type VehicleAspectFilterOptions = {
  options: ReplaceAll<APVehicleAspectOptionMap, AutoCompleteOption[]>;
  limits: Record<keyof APVehicleAspectLimitMap, APVehicleAspectLimits>;
};

export function buildVehicleAspectOptions(
  vehicleAspects: APVehicleAspects,
  availableSegments?: VehicleSegment[]
): VehicleAspectFilterOptions {
  const segments: VehicleSegment[] =
    availableSegments ?? vehicleAspects.options.segment;

  return {
    options: {
      year: buildAutoCompleteOptionsFromArray(vehicleAspects.options.year),
      class: buildAutoCompleteOptionsFromArray(
        vehicleAspects.options.class,
        VehicleClassLabelMap
      ),
      segment: buildAutoCompleteOptionsFromArray(
        segments,
        VehicleSegmentLabelMap
      ),
      powerPlantType: buildAutoCompleteOptionsFromArray(
        vehicleAspects.options.powerPlantType,
        PowerPlantLabelMap
      ),
      bodyType: buildAutoCompleteOptionsFromArray(
        vehicleAspects.options.bodyType,
        BodyTypeLabelMap
      ),
      fuelType: buildAutoCompleteOptionsFromArray(
        vehicleAspects.options.fuelType,
        FuelTypeLabelMap
      ),
      driveDescription: buildAutoCompleteOptionsFromArray(
        vehicleAspects.options.driveDescription,
        DriveDescriptionLabelMap
      ),
      make: buildAutoCompleteOptionsFromArray(vehicleAspects.options.make),
      seatCapacity: VEHICLE_SEAT_RANGE_OPTIONS,
      transmission: buildAutoCompleteOptionsFromArray(
        vehicleAspects.options.transmission,
        TransmissionLabelMap
      ),
    },
    limits: merge(DEFAULT_VEHICLE_ASPECT_LIMITS, vehicleAspects.limits),
  };
}

// Form Input -> URL Search Params
export function buildVehicleVariantSearchParams(
  input: VehicleSearchInputs
): URLSearchParams {
  const newParams = new URLSearchParams();

  if (input.year) {
    addAutoCompleteOptionToParams(newParams, "year", [input.year]);
  }

  if (input.segment) {
    addAutoCompleteOptionToParams(newParams, "segment", input.segment);
  }

  if (input.powerPlantType) {
    addAutoCompleteOptionToParams(
      newParams,
      "powerPlantType",
      input.powerPlantType
    );
  }

  if (input.bodyType) {
    addAutoCompleteOptionToParams(newParams, "bodyType", input.bodyType);
  }

  if (input.fuelType) {
    addAutoCompleteOptionToParams(newParams, "fuelType", input.fuelType);
  }

  if (input.driveDescription) {
    addAutoCompleteOptionToParams(
      newParams,
      "driveDescription",
      input.driveDescription
    );
  }

  if (input.transmission) {
    addAutoCompleteOptionToParams(
      newParams,
      "transmission",
      input.transmission
    );
  }

  if (input.make) {
    addAutoCompleteOptionToParams(newParams, "make", input.make);
  }

  if (input.priceRange) {
    addSliderRangeFilterToParams(
      newParams,
      "listPriceIncGst",
      input.priceRange
    );
  }

  if (input.seatCapacity) {
    addBetweenMultipleFilterToParams(
      newParams,
      "seatCapacity",
      input.seatCapacity
    );
  }

  if (input.minCargoVolumeRange) {
    addSliderRangeFilterToParams(
      newParams,
      "minCargoVolume",
      input.minCargoVolumeRange
    );
  }

  if (input.powerKwRange) {
    addSliderRangeFilterToParams(newParams, "powerKw", input.powerKwRange);
  }

  if (input.evRange) {
    addSliderRangeFilterToParams(newParams, "range", input.evRange);
  }

  if (input.fuelEconomyRange) {
    addSliderRangeFilterToParams(
      newParams,
      "fuelEconomy",
      input.fuelEconomyRange
    );
  }

  if (input.accelerationRange) {
    addSliderRangeFilterToParams(
      newParams,
      "acceleration",
      input.accelerationRange
    );
  }

  if (input.warrantyYearsRange) {
    addSliderRangeFilterToParams(
      newParams,
      "warrantyYears",
      input.warrantyYearsRange
    );
  }

  if (input.ancapratingRange) {
    addSliderRangeFilterToParams(
      newParams,
      "ancaprating",
      input.ancapratingRange
    );
  }

  if (input.towingUnbrakedKgRange) {
    addSliderRangeFilterToParams(
      newParams,
      "towingUnbrakedKg",
      input.towingUnbrakedKgRange
    );
  }

  if (input.co2CombinedRange) {
    addSliderRangeFilterToParams(
      newParams,
      "co2Combined",
      input.co2CombinedRange
    );
  }

  if (input.powerToWeightRange) {
    addSliderRangeFilterToParams(
      newParams,
      "powerToWeight",
      input.powerToWeightRange
    );
  }

  return newParams;
}

const VEHICLE_SEAT_RANGE_OPTIONS = [
  { id: "1to3", label: "1 to 3" },
  { id: "4to5", label: "4 to 5" },
  { id: "6to8", label: "6 to 8" },
  { id: "9+", label: "9+" },
];

// URL Search Params -> Form Input
export function buildVehicleVariantSearchInputsFromParams(
  params: URLSearchParams,
  vehicleAspectOptions: VehicleAspectFilterOptions
): VehicleSearchInputs {
  return {
    year:
      getAutoCompleteOptionFromParam(
        params,
        "year",
        vehicleAspectOptions.options.year
      ) ?? null,
    segment:
      getAutoCompleteOptionsFromParam(
        params,
        "segment",
        vehicleAspectOptions.options.segment
      ) ?? [],
    powerPlantType:
      getAutoCompleteOptionsFromParam(
        params,
        "powerPlantType",
        vehicleAspectOptions.options.powerPlantType
      ) ?? [],
    seatCapacity:
      getAutoCompleteOptionsFromParam(
        params,
        "seatCapacity",
        vehicleAspectOptions.options.seatCapacity
      ) ?? [],
    bodyType:
      getAutoCompleteOptionsFromParam(
        params,
        "bodyType",
        vehicleAspectOptions.options.bodyType
      ) ?? [],
    fuelType:
      getAutoCompleteOptionsFromParam(
        params,
        "fuelType",
        vehicleAspectOptions.options.fuelType
      ) ?? [],
    driveDescription:
      getAutoCompleteOptionsFromParam(
        params,
        "driveDescription",
        vehicleAspectOptions.options.driveDescription
      ) ?? [],
    transmission:
      getAutoCompleteOptionsFromParam(
        params,
        "transmission",
        vehicleAspectOptions.options.transmission
      ) ?? [],
    make:
      getAutoCompleteOptionsFromParam(
        params,
        "make",
        vehicleAspectOptions.options.make
      ) ?? [],
    priceRange: getSliderRangeFilterFromParam(params, "listPriceIncGst") ?? {
      type: "any",
    },
    minCargoVolumeRange: getSliderRangeFilterFromParam(
      params,
      "minCargoVolume"
    ) ?? { type: "any" },
    powerKwRange: getSliderRangeFilterFromParam(params, "powerKw") ?? {
      type: "any",
    },
    evRange: getSliderRangeFilterFromParam(params, "range") ?? { type: "any" },
    fuelEconomyRange: getSliderRangeFilterFromParam(params, "fuelEconomy") ?? {
      type: "any",
    },
    accelerationRange: getSliderRangeFilterFromParam(
      params,
      "acceleration"
    ) ?? { type: "any" },
    warrantyYearsRange: getSliderRangeFilterFromParam(
      params,
      "warrantyYears"
    ) ?? { type: "any" },
    ancapratingRange: getSliderRangeFilterFromParam(params, "ancaprating") ?? {
      type: "any",
    },
    towingUnbrakedKgRange: getSliderRangeFilterFromParam(
      params,
      "towingUnbrakedKg"
    ) ?? { type: "any" },
    co2CombinedRange: getSliderRangeFilterFromParam(params, "co2Combined") ?? {
      type: "any",
    },
    powerToWeightRange: getSliderRangeFilterFromParam(
      params,
      "powerToWeight"
    ) ?? { type: "any" },
  };
}

function getSortFromParams(
  params: URLSearchParams
): Partial<Record<keyof VehicleVariantSearchSortOptions, "asc" | "desc">> {
  const param = params.get("sort") as VehicleVariantSearchSortOption;

  if (!param) {
    return {
      listPriceIncGst: "desc",
    };
  }

  switch (param) {
    case "priceAsc":
      return { listPriceIncGst: "asc" };
    case "priceDesc":
      return { listPriceIncGst: "desc" };
    case "evRangeDesc":
      return { range: "desc" };
    case "powerKwDesc":
      return { powerKw: "desc" };
    case "cargoMinDesc":
      return { minCargoVolume: "desc" };
    case "makeAsc":
      return { make: "asc" };
    case "makeDesc":
      return { make: "desc" };
  }
}

// URL Search Params -> API Search Filters
export function buildVehicleVariantSearchQueryFromParams(
  params: URLSearchParams,
  pageSize: number = 15
): VehicleVariantSearchFilters {
  return {
    filters: {
      year: getSearchFilterFromParam(params, "year", true),
      class: getSearchFilterFromParam(params, "class", true),
      segment: getSearchFilterFromParam(params, "segment", true),
      powerPlantType: getSearchFilterFromParam(params, "powerPlantType", true),
      bodyType: getSearchFilterFromParam(params, "bodyType", true),
      fuelType: getSearchFilterFromParam(params, "fuelType", true),
      driveDescription: getSearchFilterFromParam(
        params,
        "driveDescription",
        true
      ),
      transmission: getSearchFilterFromParam(params, "transmission", true),
      make: getSearchFilterFromParam(params, "make", true),
      listPriceIncGst: getSearchFilterFromParam(params, "listPriceIncGst"),
      seatCapacity: getSearchFilterFromParam(params, "seatCapacity", true),
      minCargoVolume: getSearchFilterFromParam(params, "minCargoVolume"),
      powerKw: getSearchFilterFromParam(params, "powerKw"),
      range: getSearchFilterFromParam(params, "range"),
      fuelEconomy: getSearchFilterFromParam(params, "fuelEconomy"),
      acceleration: getSearchFilterFromParam(params, "acceleration"),
      warrantyYears: getSearchFilterFromParam(params, "warrantyYears"),
      ancaprating: getSearchFilterFromParam(params, "ancaprating", true),
      towingUnbrakedKg: getSearchFilterFromParam(params, "towingUnbrakedKg"),
      co2Combined: getSearchFilterFromParam(params, "co2Combined"),
      powerToWeight: getSearchFilterFromParam(params, "powerToWeight"),
    },
    sort: getSortFromParams(params),
    pageSize: pageSize,
    nextPageLink: undefined,
  };
}

export const isPowerPlantTypeElectric = (powerPlantType: PowerPlantType) => {
  return ["bev", "hev", "phev"].includes(powerPlantType);
};

export const standardValueFormatter = (
  value: unknown,
  unit: string | null = null,
  numberFormatter?: "thousands" | "dollars",
  decimalPlaces: number = 0
) => {
  let renderedValue: string = `${value}`;

  if (numberFormatter === "dollars") {
    renderedValue = toDollars(value as number, decimalPlaces);
  } else if (numberFormatter === "thousands") {
    renderedValue = toFormattedNumber(value as number, decimalPlaces);
  }

  return value ? `${renderedValue}${unit ? ` ${unit}` : ""}` : "-";
};

export const standardVehicleValueFormatter = (
  key: keyof APVehicleVariant,
  value: unknown,
  options: StandardVehicleValueFormatterOptions = {}
) => {
  const labelMapper = APVehicleAspectValueLabelMap[key];
  const defaultOptions = APVehicleValueFormatterOptionsMap[key] ?? {};
  const mergedOptions = merge(defaultOptions, options);

  if (labelMapper) {
    const label = mapLabelWithFallback(
      key,
      value as string | null,
      labelMapper,
      mergedOptions.fallbacks ?? {}
    );

    return label || "-";
  } else {
    const unit = APVehicleAspectUnitMap[key];

    return standardValueFormatter(
      value,
      unit,
      mergedOptions.numberFormatter,
      mergedOptions.decimalPlaces
    );
  }
};

export const formatReleaseDate = (
  releaseYear: string,
  releaseMonth: string | null
) => {
  return !releaseMonth
    ? toYearOnlyFormat(releaseYear)
    : `${toYearMonthOnlyFormat(`${releaseYear}-${releaseMonth}`)}`;
};

export const mergeAndSortVehicleOptions = (
  listA: VehicleOption[],
  listB: VehicleOption[]
) => {
  return [...listA, ...listB].sort((a, b) => {
    return a.displayOrder - b.displayOrder;
  });
};
